"use client";
import { useState } from "react";
import Image, { StaticImageData } from "next/image";
import { Box, Stack, SimpleGrid, Flex, GridItem, Dialog, List } from "@chakra-ui/react";
import { PiMagnifyingGlassPlusBold } from "@onemind-services-llc/react-icons-ng/pi";
import { useHeadingVariant, useTextVariant } from "@/theme/hooks/useSectionStyle";
import { Heading, Text } from "@/elements";
import PageLayout from "@/layouts/pageLayout";
import BgImage from "public/shared/magnifyImageframeBg.png";

type FrameProps = {
  bgImage?: StaticImageData;
  title: string;
  description?: string;
  image: StaticImageData;
  magnifiedImage?: StaticImageData;
  noteText?: {
    label: string;
    text: string;
  };
  list: {
    label: string;
    subLabel?: string;
    subText?: string;
    text?: string;
    points?: string[];
    children?: React.ReactNode;
    concText?: string;
  }[];
  maxW?: string | number;
};

const MagnifiedImageFrame = (props: FrameProps) => {
  const {
    noteText,
    bgImage = BgImage,
    title,
    description,
    list,
    image,
    magnifiedImage,
    maxW = "auto",
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const TextVariant = useTextVariant();
  const headingVariant = useHeadingVariant();

  return (
    <Box position="relative" bgColor="#EFF4F9" mt={16}>
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgImage={`url(${bgImage.src})`}
        backgroundPosition="center"
        bgSize="cover"
        opacity={0.51}
        zIndex={0}
      />
      <Box position="relative" zIndex={1} py={{ base: 8, md: 16, lg: 20 }}>
        <PageLayout marginTop={0}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={"1vw"}>
            <GridItem h="100%">
              <Stack h="100%">
                <Flex align="start" justify="center" gap={3}>
                  <Box width="6px" height="100%" bg="#1E87F0" pt={4} />
                  <Heading as="h2" variant={headingVariant} whiteSpace={"pre-line"}>
                    {title}
                  </Heading>
                </Flex>
                <Box
                  position="relative"
                  flex="1"
                  display="flex"
                  alignItems="center"
                  justifyContent="left"
                  pr={{ lg: 6 }}
                  maxW={maxW}
                  margin={maxW === "auto" ? undefined : "0 auto"}
                >
                  <Image
                    src={image}
                    alt="OneMind Infographic"
                    width={500}
                    height={500}
                    priority
                    style={{
                      width: "100%",
                      cursor: magnifiedImage ? "pointer" : "default",
                      height: "80%",
                    }}
                    onClick={magnifiedImage ? onOpen : undefined}
                  />
                  {magnifiedImage && (
                    <Box
                      position="absolute"
                      top={10}
                      right={10}
                      bgColor="primary.200"
                      p={1}
                      borderRadius={"4px"}
                    >
                      <PiMagnifyingGlassPlusBold
                        color="white"
                        size={28}
                        style={{ cursor: "pointer" }}
                        onClick={onOpen}
                      />
                    </Box>
                  )}
                </Box>
                {noteText && (
                  <Box>
                    <Text as="span" variant={TextVariant} fontWeight={600}>
                      {noteText.label}
                    </Text>
                    <Text as="span" variant={TextVariant}>
                      {" "}
                      : {noteText.text}
                    </Text>
                  </Box>
                )}
              </Stack>
            </GridItem>
            <GridItem pl={{ lg: 8 }}>
              {description && (
                <Text variant={TextVariant} color="text.100" mb={8}>
                  {description}
                </Text>
              )}
              <Stack gap={6}>
                {list.map((item, index) => (
                  <Stack key={index} gap={2}>
                    <Text variant={TextVariant} fontWeight={600}>
                      {item.label}
                    </Text>
                    <Stack gap={0}>
                      {item.subLabel && (
                        <Text variant={TextVariant} color="text.100" fontStyle={"italic"}>
                          {item.subLabel}
                        </Text>
                      )}
                      {item.subText && (
                        <Text
                          variant={TextVariant}
                          fontSize={{ base: "14px", md: "16px" }}
                          color="text.100"
                        >
                          ({item.subText})
                        </Text>
                      )}
                    </Stack>
                    {item.text && (
                      <Text variant={TextVariant} color="text.100">
                        {item.text}
                      </Text>
                    )}
                    {item.points && (
                      <List.Root gap={1} pl={4} listStyleType="disc">
                        {item.points.map((point, pointIndex) => (
                          <List.Item key={pointIndex}>
                            <Text variant={TextVariant} color="text.100">
                              {point}
                            </Text>
                          </List.Item>
                        ))}
                      </List.Root>
                    )}
                    {item.children && item.children}
                    {item.concText && (
                      <Text variant={TextVariant} color="text.100">
                        {item.concText}
                      </Text>
                    )}
                  </Stack>
                ))}
              </Stack>
            </GridItem>
          </SimpleGrid>
        </PageLayout>

        {magnifiedImage && (
          <Dialog.Root open={isOpen} onOpenChange={({ open }) => !open && onClose()}>
            <Dialog.Backdrop bg="blackAlpha.800" />
            <Dialog.Positioner display="flex" alignItems="center" justifyContent="center">
              <Dialog.Content
                bg="transparent"
                boxShadow="none"
                p={4}
                maxW="60vw"
                maxH="60vh"
              >
                <Dialog.CloseTrigger
                  color="white"
                  top="2"
                  right="2"
                  bg="blackAlpha.700"
                  _hover={{ bg: "blackAlpha.900" }}
                  borderRadius="full"
                  position="absolute"
                  width="40px"
                  height="40px"
                  fontSize="20px"
                  fontWeight="bold"
                  cursor="pointer"
                >
                  X
                </Dialog.CloseTrigger>
                <Image
                  src={magnifiedImage}
                  alt="Full-size OneMind Infographic"
                  width={1200}
                  height={800}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Root>
        )}
      </Box>
    </Box>
  );
};

export default MagnifiedImageFrame;
